import { Suspense, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Send, Loader2, AlertCircle, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardContent, Checkbox, Input, Label } from '@higgins/ui';
import { SWMS_TYPES, invalidEmails, parseEmailList, type SwmsType } from '../lib/types';
import { postShare, getWorkOrders, ApiError, type WorkOrderOption } from '../lib/api';
import {
  isSendingAtom, jobCodeAtom, meAtom, recentSubbiesAtom, rememberRecipientsAtom,
  resetFormAtom, selectedSwmsAtom, selectedWorkOrderAtom, subbieEmailAtom,
  swmsCatalogueAtom, swmsTypeAtom,
} from '../store/atoms';

const MAX_JOB_CODE_LENGTH = 20;
const SUCCESS_TOAST_MS = 8000;
/** Longer when SWMS were left alone, so the PM has time to read which. */
const SUCCESS_WITH_KEPT_TOAST_MS = 15000;

/** Segmented Building / Painting switch. Full-width so it is easy to hit on a phone. */
function LibraryToggle() {
  const [swmsType, setSwmsType] = useAtom(swmsTypeAtom);
  const setSelected = useSetAtom(selectedSwmsAtom);

  return (
    <div role="group" aria-label="SWMS library" className="grid grid-cols-2 gap-1 rounded-lg bg-slate-200/70 p-1">
      {SWMS_TYPES.map((type) => {
        const active = type === swmsType;
        return (
          <button
            key={type}
            type="button"
            aria-pressed={active}
            onClick={() => {
              // The two libraries have different SWMS, so a switch clears the picks.
              setSwmsType(type);
              setSelected([]);
            }}
            className={`min-h-11 rounded-md px-3 text-sm font-medium transition ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {type}
          </button>
        );
      })}
    </div>
  );
}

function SwmsChecklist({ swmsType }: { swmsType: SwmsType }) {
  const catalogue = useAtomValue(swmsCatalogueAtom);
  const [selected, setSelected] = useAtom(selectedSwmsAtom);
  const options = catalogue[swmsType];

  const toggle = (name: string, checked: boolean) => {
    setSelected((current) =>
      checked ? [...current, name] : current.filter((item) => item !== name)
    );
  };

  return (
    <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
      {options.map((name) => {
        const checked = selected.includes(name);
        return (
          <label
            key={name}
            className="flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2.5 active:bg-slate-50"
          >
            <Checkbox checked={checked} onCheckedChange={(value) => toggle(name, value === true)} />
            <span className="text-sm leading-snug text-slate-800">{name}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Open work orders for the entered job, as tap-to-pick rows (issue #144).
 * Auto-selects when there is exactly one; skippable when there are none or the
 * user deselects. The chosen WO is stamped hig_sitefileshared at share time.
 */
function WorkOrderPicker() {
  const jobCode = useAtomValue(jobCodeAtom);
  const [selected, setSelected] = useAtom(selectedWorkOrderAtom);
  const [options, setOptions] = useState<WorkOrderOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelected(null);
    setOptions([]);
    const code = jobCode.trim();
    if (code.length < 4) return;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { workOrders } = await getWorkOrders(code);
        setOptions(workOrders);
        if (workOrders.length === 1) setSelected(workOrders[0]);
      } catch {
        setOptions([]); // lookup failure never blocks sharing
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => { clearTimeout(timer); setLoading(false); };
  }, [jobCode, setSelected]);

  if (jobCode.trim().length < 4) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <Label>Work order</Label>
        <span className="text-xs text-slate-500">
          {loading ? 'Looking up…' : options.length === 0 ? 'None open — optional' : selected ? selected.name : 'Tap to pick (optional)'}
        </span>
      </div>
      {options.length > 0 && (
        <div className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
          {options.map((wo) => {
            const active = selected?.id === wo.id;
            return (
              <button
                key={wo.id}
                type="button"
                onClick={() => setSelected(active ? null : wo)}
                className={`flex min-h-12 w-full items-center justify-between gap-3 px-3 py-2.5 text-left ${
                  active ? 'bg-orange-50' : 'active:bg-slate-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900">{wo.name}</span>
                  {wo.detail && <span className="block truncate text-xs text-slate-500">{wo.detail}</span>}
                </span>
                {active && <CheckCircle2 className="h-4 w-4 shrink-0 text-orange-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Entitlement gate: a friendly request-access card instead of a 403 on submit. */
function EntitlementGate({ children }: { children: React.ReactNode }) {
  const me = useAtomValue(meAtom);
  if (me.entitled) return <>{children}</>;
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <ShieldAlert className="h-10 w-10 text-orange-500" />
        <p className="font-semibold text-slate-900">You don&apos;t have access yet</p>
        <p className="max-w-sm text-sm text-slate-600">
          Site File Sharing is limited to authorised access groups (Works, PMO
          and IT). Contact IT to request access — mention your role involves
          sharing SWMS with subcontractors.
        </p>
      </CardContent>
    </Card>
  );
}

export default function SharePage() {
  const swmsType = useAtomValue(swmsTypeAtom);
  const [jobCode, setJobCode] = useAtom(jobCodeAtom);
  const [subbieEmail, setSubbieEmail] = useAtom(subbieEmailAtom);
  const selected = useAtomValue(selectedSwmsAtom);
  const [isSending, setIsSending] = useAtom(isSendingAtom);
  const resetForm = useSetAtom(resetFormAtom);
  const recentSubbies = useAtomValue(recentSubbiesAtom);
  const rememberRecipients = useSetAtom(rememberRecipientsAtom);
  const selectedWorkOrder = useAtomValue(selectedWorkOrderAtom);

  const currentAddresses = parseEmailList(subbieEmail);
  const suggestions = recentSubbies.filter((a) => !currentAddresses.includes(a));

  function addRecipient(address: string) {
    setSubbieEmail(currentAddresses.length ? `${currentAddresses.join(', ')}, ${address}` : address);
  }

  const badEmails = useMemo(() => invalidEmails(subbieEmail), [subbieEmail]);

  const canSend =
    jobCode.trim() !== '' &&
    selected.length > 0 &&
    parseEmailList(subbieEmail).length > 0 &&
    badEmails.length === 0 &&
    !isSending;

  async function handleSend() {
    setIsSending(true);
    try {
      const result = await postShare({
        swmsType,
        jobCode: jobCode.trim(),
        swms: selected,
        subbieEmails: parseEmailList(subbieEmail),
        workOrderId: selectedWorkOrder?.id,
        workOrderName: selectedWorkOrder?.name,
      });
      rememberRecipients(result.emailedTo);
      const total = result.copied.length + result.queued.length;
      const queuedNote = result.queued.length
        ? ` (${result.queued.length} still copying -- they'll appear in the folder shortly)`
        : '';
      const woNote =
        result.workOrderStamped === 'stamped' ? ` Work order ${result.workOrder} marked as shared.` : '';
      const keptNote = result.kept.length
        ? ` Kept the job's edited copy of ${result.kept.join(', ')} -- not replaced with the library version.`
        : '';
      const notCheckedNote = result.notChecked.length
        ? ` Couldn't check the job's copy of ${result.notChecked.join(', ')}, so left it as it was -- try again to refresh it.`
        : '';
      const sentNote = total
        ? `${total} document${total === 1 ? '' : 's'} sent to the Site File${queuedNote} and the link`
        : 'The Site File link';
      const leftAlone = result.kept.length + result.notChecked.length;
      toast.success(`SWMS shared for ${result.jobCode}`, {
        description:
          `${sentNote} emailed to ${result.emailedTo.join(', ')}. You are CC'd.` +
          keptNote + notCheckedNote + woNote,
        duration: leftAlone ? SUCCESS_WITH_KEPT_TOAST_MS : SUCCESS_TOAST_MS,
      });
      resetForm();
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Something went wrong.';
      toast.error('Could not share the SWMS', { description: detail, duration: 10000 });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <EntitlementGate>
      <div className="space-y-5">
        <LibraryToggle />

        <Card>
          <CardContent className="space-y-5 pt-5">
            <div className="space-y-1.5">
              <Label htmlFor="jobCode">Job code</Label>
              <Input
                id="jobCode"
                value={jobCode}
                maxLength={MAX_JOB_CODE_LENGTH}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="e.g. VN1234"
                onChange={(event) => setJobCode(event.target.value.toUpperCase())}
                className="min-h-11"
              />
              <p className="text-xs text-slate-500">
                Used to find the job&apos;s SharePoint site. You&apos;ll be told straight away if it can&apos;t be found.
              </p>
            </div>

            <WorkOrderPicker />

            <div className="space-y-1.5">
              <Label htmlFor="subbieEmail">Subcontractor email</Label>
              <Input
                id="subbieEmail"
                name="subbieEmail"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={subbieEmail}
                placeholder="name@example.com, second@example.com"
                onChange={(event) => setSubbieEmail(event.target.value)}
                className="min-h-11"
              />
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {suggestions.map((address) => (
                    <button
                      key={address}
                      type="button"
                      onClick={() => addRecipient(address)}
                      className="max-w-full truncate rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 active:bg-slate-100"
                    >
                      {address}
                    </button>
                  ))}
                </div>
              )}
              {badEmails.length > 0 ? (
                <p className="flex items-start gap-1.5 text-xs text-red-600">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Check these addresses: {badEmails.join(', ')}</span>
                </p>
              ) : (
                <p className="text-xs text-slate-500">Separate multiple addresses with commas.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label>SWMS to share</Label>
                <span className="text-xs text-slate-500">{selected.length} selected</span>
              </div>
              <Suspense
                fallback={
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-6 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading SWMS…
                  </div>
                }
              >
                <SwmsChecklist swmsType={swmsType} />
              </Suspense>
            </div>
          </CardContent>
        </Card>

        {/* Sticky footer so Send is always reachable without scrolling back up. */}
        <div className="sticky bottom-0 -mx-4 border-t border-slate-200/60 bg-white/90 px-4 py-3 backdrop-blur-sm">
          <Button onClick={handleSend} disabled={!canSend} className="min-h-12 w-full text-base">
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sharing… this can take a minute
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" /> Share SWMS
              </>
            )}
          </Button>
        </div>
      </div>
    </EntitlementGate>
  );
}
